import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Clock, LogOut, Maximize, Minimize, Moon, Settings, Sun, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { billingService, getErrorMessage } from '../services/api';
import type { BillingInfo } from '../types/api';
import { notePaymentsConfigured, openBillingPortal } from '../utils/checkout';
import { formatDate, formatMinutes, formatResetTime } from '../utils/format';
import AccountSettings from './AccountSettings';
import { useToast } from './Toast';
import { Spinner } from './ui';
import PaymentsNote from './PaymentsNote';
import TrialRequest from './TrialRequest';
import UpgradeButton from './UpgradeButton';

// A daily allowance: "10 min", "2 h".
const perDay = (seconds: number) => (seconds >= 3600 && seconds % 3600 === 0 ? `${seconds / 3600} h` : formatMinutes(seconds));

// The user's menu (top right of every page): who they are, their simulation time today and plan, the theme, account
// settings and logging out, in a panel from the right.
const AccountMenu: React.FC<{ roleLabel?: string }> = ({ roleLabel }) => {
  const { user, logout } = useAuth();
  const { isDarkMode, setDarkMode } = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [billingError, setBillingError] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  // Full screen (the whole app, without the browser's bars): offered where the browser allows it (not iPhones).
  const canFullScreen = typeof document !== 'undefined' && !!document.fullscreenEnabled;
  const [isFullScreen, setIsFullScreen] = useState(() => typeof document !== 'undefined' && !!document.fullscreenElement);
  useEffect(() => {
    const onChange = () => setIsFullScreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggleFullScreen = () => {
    const request = document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen();
    request.catch(() => toast.error('Full screen is not available here.'));
  };

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    setBillingError(false);
    // Fresh each time: the time left changes while a simulation runs.
    billingService.me().then((loaded) => { notePaymentsConfigured(loaded); setBilling(loaded); }).catch(() => setBillingError(true));
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  if (!user) return null;
  const initial = user.full_name?.charAt(0).toUpperCase() || '?';
  const role = roleLabel ?? user.role;

  const handleLogout = () => {
    setOpen(false);
    logout();
    // Explicitly to the bare /login: ProtectedRoute would otherwise add ?next= for the page being left.
    navigate('/login', { replace: true });
  };

  const allowance = billing?.allowance;
  const usedFraction = allowance && allowance.limit_seconds ? Math.min(1, allowance.used_seconds / allowance.limit_seconds) : 0;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Account menu"
        title="Account"
        className="flex items-center gap-2.5 rounded-lg p-1 lg:pr-3 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <span className="avatar" aria-hidden="true">{initial}</span>
        <span className="hidden lg:block text-left leading-tight">
          <span className="block text-sm font-semibold text-gray-900 dark:text-white">{user.full_name}</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400 capitalize">{role}</span>
        </span>
      </button>

      {/* Portals: the top bar's backdrop blur would make it the containing block of anything fixed inside it. */}
      {open && createPortal(
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-gray-950/40 backdrop-blur-[1px]" onClick={close} aria-hidden="true" />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="absolute right-0 top-0 flex h-dvh w-full max-w-sm flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 shadow-xl"
          >
            <div className="flex items-start gap-3 border-b border-gray-200 dark:border-gray-800 p-4">
              <span className="avatar h-11 w-11 text-base" aria-hidden="true">{initial}</span>
              <div className="min-w-0 flex-1">
                <h2 id={titleId} className="truncate text-base font-semibold text-gray-900 dark:text-white">{user.full_name}</h2>
                <p className="truncate text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{role}</p>
              </div>
              <button ref={closeRef} type="button" onClick={close} className="btn btn-ghost btn-icon -mr-2 -mt-1" aria-label="Close menu">
                <X />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              <section aria-labelledby={`${titleId}-time`}>
                <h3 id={`${titleId}-time`} className="eyebrow mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Simulation time</h3>
                {billingError ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Your simulation time could not be loaded.</p>
                ) : !billing || !allowance ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"><Spinner className="w-4 h-4" /> Loading...</div>
                ) : allowance.unlimited ? (
                  <p className="text-sm text-gray-700 dark:text-gray-300">Unlimited</p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-900 dark:text-white">
                      <span className="text-2xl font-semibold tabular-nums" data-testid="account-time-left">{formatMinutes(allowance.remaining_seconds ?? 0)}</span>
                      <span className="text-gray-500 dark:text-gray-400"> left today of {formatMinutes(allowance.limit_seconds ?? 0)}</span>
                    </p>
                    <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden" role="progressbar" aria-label="Simulation time used today"
                      aria-valuemin={0} aria-valuemax={allowance.limit_seconds ?? 0} aria-valuenow={allowance.used_seconds}>
                      <div className={`h-full rounded-full ${usedFraction >= 1 ? 'bg-amber-500' : 'bg-blue-600'}`} style={{ width: `${usedFraction * 100}%` }} />
                    </div>
                    <p className="hint mt-0">More time {formatResetTime(allowance.resets_at)}. Your classes' lessons may have their own time.</p>
                  </div>
                )}
                {/* PLAN-3: a paid class's lessons run on its own daily time, separate from the user's. */}
                {billing?.class_time?.map((classTime) => (
                  <p key={classTime.class_id} className="mt-3 text-sm text-gray-700 dark:text-gray-300" data-testid="account-class-time">
                    <span className="font-medium">{classTime.name}</span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {' · '}{formatMinutes(classTime.remaining_seconds ?? 0)} left today of {perDay(classTime.limit_seconds ?? 0)} for the class's lessons
                    </span>
                  </p>
                ))}
                {billing && billing.plan !== 'admin' && (
                  <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-800 p-3 text-sm">
                    {billing.plan === 'student' ? (
                      <>
                        <p className="font-semibold text-gray-900 dark:text-white">Student plan</p>
                        <p className="text-gray-500 dark:text-gray-400">
                          {perDay(billing.limits.student_seconds_per_day)} a day
                          {billing.plan_period_end && <> · renews or ends {formatDate(billing.plan_period_end)}</>}
                        </p>
                        {billing.checkout?.can_manage && (
                          <button type="button" className="link mt-2 text-sm" onClick={() => openBillingPortal().catch((error) => toast.error(getErrorMessage(error)))}>
                            Manage subscription
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="font-semibold text-gray-900 dark:text-white">Free plan</p>
                        <p className="text-gray-500 dark:text-gray-400 mb-3">{perDay(billing.limits.free_seconds_per_day)} of simulation a day.</p>
                        {billing.checkout ? (
                          <UpgradeButton plan="student" className="btn btn-primary w-full">
                            Student plan · {perDay(billing.limits.student_seconds_per_day)} a day for €{billing.prices.student.amount}/month
                          </UpgradeButton>
                        ) : (
                          // PLAN-18: until online payment is on, a free month instead.
                          <TrialRequest
                            plan="student"
                            offer={<span className="font-medium text-gray-900 dark:text-white">Student plan · {perDay(billing.limits.student_seconds_per_day)} a day for €{billing.prices.student.amount}/month</span>}
                            noteClassName="mt-1 text-gray-500 dark:text-gray-400"
                            buttonClassName="btn btn-primary w-full"
                          />
                        )}
                      </>
                    )}
                    <PaymentsNote className="mt-3 text-xs text-gray-500 dark:text-gray-400" />
                  </div>
                )}
              </section>

              <section aria-labelledby={`${titleId}-theme`}>
                <h3 id={`${titleId}-theme`} className="eyebrow mb-2">Display</h3>
                <div className="flex gap-2">
                  <div className="grid flex-1 grid-cols-2 gap-1 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-100/80 dark:bg-gray-950 p-1" role="radiogroup" aria-label="Theme">
                    {[
                      { dark: false, label: 'Light', Icon: Sun },
                      { dark: true, label: 'Dark', Icon: Moon },
                    ].map(({ dark, label, Icon }) => (
                      <button
                        key={label}
                        type="button"
                        role="radio"
                        aria-checked={isDarkMode === dark}
                        onClick={() => setDarkMode(dark)}
                        className={`flex min-h-[2.5rem] items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors ${isDarkMode === dark
                          ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white'
                          : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
                      >
                        <Icon className="w-4 h-4" /> {label}
                      </button>
                    ))}
                  </div>
                  {canFullScreen && (
                    <button
                      type="button"
                      onClick={toggleFullScreen}
                      aria-pressed={isFullScreen}
                      className="btn btn-secondary shrink-0 min-h-[3rem] px-3"
                      title={isFullScreen ? 'Exit full screen' : 'Full screen'}
                    >
                      {isFullScreen ? <Minimize /> : <Maximize />}
                      {isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
                    </button>
                  )}
                </div>
              </section>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-800 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
              <button type="button" className="btn btn-ghost w-full justify-start" onClick={() => { setOpen(false); setSettingsOpen(true); }}>
                <Settings /> Account Settings
              </button>
              <button type="button" className="btn btn-ghost w-full justify-start" onClick={handleLogout}>
                <LogOut /> Logout
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {settingsOpen && createPortal(<AccountSettings onClose={() => { setSettingsOpen(false); triggerRef.current?.focus(); }} />, document.body)}
    </>
  );
};

export default AccountMenu;
